import React, { createContext, useContext, useState, useCallback } from "react";
import { STUDY_STRINGS } from "@/lib/studyI18nStrings";

type Language = "es" | "en";

interface LanguageContextType {
  lang: Language;
  toggleLang: () => void;
  t: (key: string) => string;
}

const translations: Record<string, Record<Language, string>> = {
  // Nav
  "nav.disciplines": { es: "Disciplinas", en: "Disciplines" },
  "nav.minds": { es: "Grandes Mentes", en: "Great Minds" },
  "nav.chat": { es: "Chat · Inicio", en: "Chat · Home" },
  "nav.workshop": { es: "Taller", en: "Workshop" },
  "nav.tools": { es: "Herramientas", en: "Tools" },
  "nav.workshopLegacy": { es: "Taller", en: "Workshop" },
  "nav.toolsLegacy": { es: "Herram.", en: "Tools" },
  "nav.dashboard": { es: "Proyectos", en: "Projects" },
  "nav.brain": { es: "Cerebro 3D", en: "3D Brain" },
  "nav.join": { es: "Únete al Lab", en: "Join the Lab" },
  "hero.ctaChat": { es: "Abrir Chat OS", en: "Open Chat OS" },

  // Hero
  "hero.badge": { es: "Lab · 001 / Encendido del fuego", en: "Lab · 001 / Lighting the Fire" },
  "hero.title1": { es: "Robamos el fuego", en: "We stole the fire" },
  "hero.title2": { es: "para devolverlo", en: "to return it" },
  "hero.title3": { es: "a las manos humanas.", en: "to human hands." },
  "hero.description": {
    es: "Prometheus Lab AI es una escuela-taller donde la ingeniería, la física, la química, la biología y los oficios —electricidad, plomería, construcción, energías renovables— se funden con impresión 3D y pensamiento inventivo. Aprendes el método de las grandes mentes. Reconstruyes sus inventos. Creas los tuyos.",
    en: "Prometheus Lab AI is a workshop-school where engineering, physics, chemistry, biology and trades —electrical, plumbing, construction, renewable energy— merge with 3D printing and inventive thinking. You learn the method of great minds. You rebuild their inventions. You create your own."
  },
  "hero.cta1": { es: "Enciende tu laboratorio", en: "Light your laboratory" },
  "hero.cta2": { es: "Explora el método", en: "Explore the method" },
  "hero.stat.disciplines": { es: "Disciplinas", en: "Disciplines" },
  "hero.stat.inventors": { es: "Inventores", en: "Inventors" },
  "hero.stat.fabrication": { es: "Fabricación", en: "Fabrication" },

  // Dashboard
  "dash.title": { es: "Panel de Control", en: "Dashboard" },
  "dash.welcome": { es: "Bienvenido al Lab, Creador", en: "Welcome to the Lab, Maker" },
  "dash.projects": { es: "Proyectos Registrados", en: "Registered Projects" },
  "dash.logs": { es: "Entradas de Bitácora", en: "Journal Entries" },
  "dash.lowStock": { es: "Items Stock Bajo", en: "Low Stock Items" },
  "dash.safety": { es: "Seguridad Verificada", en: "Safety Verified" },
  "dash.recentProjects": { es: "Proyectos Recientes", en: "Recent Projects" },
  "dash.stockAlerts": { es: "Alertas de Inventario", en: "Inventory Alerts" },
  "dash.quickActions": { es: "Acciones Rápidas", en: "Quick Actions" },
  "dash.newLog": { es: "Nueva Entrada de Bitácora", en: "New Journal Entry" },
  "dash.scanCamera": { es: "Escanear con Cámara", en: "Scan with Camera" },
  "dash.generateProject": { es: "Generar Proyecto con IA", en: "Generate AI Project" },
  "dash.goToTools": { es: "Ir a Herramientas", en: "Go to Tools" },
  "dash.goToInventory": { es: "Ir a Inventario", en: "Go to Inventory" },
  "dash.viewAll": { es: "Ver todos", en: "View all" },
  "dash.noAlerts": { es: "Todo el inventario está abastecido", en: "All inventory is stocked" },
  "dash.unit": { es: "restantes", en: "remaining" },

  // MakerSuite tabs
  "tab.cad": { es: "Plano 2D", en: "2D Blueprint" },
  "tab.mathcode": { es: "Código y Matemáticas", en: "Code & Math" },
  "tab.3d": { es: "Inspección 3D", en: "3D Inspection" },
  "tab.gear": { es: "Engranajes", en: "Gears" },
  "tab.slicer": { es: "Slicer & Filamento", en: "Slicer & Filament" },
  "tab.troubleshoot": { es: "Diagnóstico 3D", en: "3D Diagnostics" },
  "tab.circuit": { es: "Circuitos & CA", en: "Circuits & AC" },
  "tab.arduino": { es: "Arduino / ESP32", en: "Arduino / ESP32" },
  "tab.materials": { es: "Matriz Materiales", en: "Materials Matrix" },
  "tab.architecture": { es: "Estructuras", en: "Structures" },
  "tab.camera": { es: "Cámara AR", en: "AR Camera" },
  "tab.journal": { es: "Bitácora", en: "Journal" },
  "tab.inventory": { es: "Inventario", en: "Inventory" },
  "tab.safety": { es: "Seguridad", en: "Safety" },
  "tab.science": { es: "Laboratorio Ciencia", en: "Science Lab" },
  "tab.solar": { es: "Solar PR", en: "Solar PR" },
  "tab.serial": { es: "Serial USB", en: "Serial USB" },
  "tab.gcode": { es: "G-Code CNC", en: "G-Code CNC" },
  "tab.periodictable": { es: "Tabla Periódica & Fórmulas", en: "Periodic Table & Formulas" },

  // MakerSuite header
  "maker.title": { es: "Utilidades de", en: "Manufacturing" },
  "maker.titleAccent": { es: "Fabricación", en: "Utilities" },
  "maker.description": {
    es: "Diseña engranajes, programa microcontroladores Arduino, consulta la matriz de materiales, modela esquemas en 2D/3D y simula circuitos de Ley de Ohm antes de construir tus prototipos.",
    en: "Design gears, program Arduino microcontrollers, consult the materials matrix, model 2D/3D schematics and simulate Ohm's Law circuits before building your prototypes."
  },

  // Chat
  "chat.placeholder": { es: "Escribe tu mensaje...", en: "Type your message..." },
  "chat.send": { es: "Enviar", en: "Send" },
  "chat.thinking": { es: "Pensando...", en: "Thinking..." },

  // Common
  "common.loading": { es: "Cargando...", en: "Loading..." },
  "common.error": { es: "Error", en: "Error" },
  "common.save": { es: "Guardar", en: "Save" },
  "common.cancel": { es: "Cancelar", en: "Cancel" },
  "common.back": { es: "Volver", en: "Back" },

  // Mesa estudio (SDD 005 T10) — interpolated via useStudyI18n
  ...STUDY_STRINGS,
};

const LanguageContext = createContext<LanguageContextType>({
  lang: "es",
  toggleLang: () => {},
  t: (key: string) => key,
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Language>(() => {
    try {
      return (localStorage.getItem("prometheus-lang") as Language) || "es";
    } catch {
      return "es";
    }
  });

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next = prev === "es" ? "en" : "es";
      try { localStorage.setItem("prometheus-lang", next); } catch {}
      return next;
    });
  }, []);

  const t = useCallback((key: string): string => {
    return translations[key]?.[lang] || key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
